/**
 * 設定 → 単位（#164、docs/66 §4。/settings/units。設定のメニューの節「項目と言葉」）。書くのは管理者だけ（担当者は見るだけ）。
 *   ・上に［単位を追加］［標準に戻す］。下に件数「20件／上限 50件」（上限なら［単位を追加］は押せない）
 *   ・一覧の行：PC（768px〜）＝左に ≡、単位名、右に［名前変更］［削除］
 *     ≡ をつかんで動かす（押すと「一番上へ／上へ／下へ／一番下へ」のメニュー＝ドラッグだけにしない）
 *   ・スマホ（幅 767px 以下）＝行のボタンは［↑］［↓］の2つだけ（横に並べるボタンは2つまで）。単位名を押すと下からシート（題＝単位名・閉じるは ×）で
 *     ［名前変更］［削除］を選ぶ。そのあとは PC と同じ（名前変更のシート・削除の確かめの窓）
 *   ・追加・名前変更はシート（単位の欄1つ。placeholder なし）。決まりは contract の unitNameError（API と同じ）。間違いは欄の下に朱の1行
 *   ・削除・標準に戻すは確かめの窓（［決定］［取消］）。削除の窓には「作成済みの書類の単位は変わりません」
 * 状態の class は is-（is-dragging・is-dropabove・is-dropbelow・is-open・is-full）
 */
import { UNIT_LIMIT, UNIT_MSG, unitNameError, type CompanyUnit, type CompanyUnitList } from "@genbaryoku/contract";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { api, messageOf } from "../api.js";
import { useMe } from "../auth.js";
import { useBackNav, usePageName } from "../components/BackButton.jsx";
import { Chevron, GripButton, GroupDangerRow, GroupInputRow, GroupList, GroupSection, MoveMenu, useFlip, useRowDrag, type MoveKind } from "../components/GroupList.jsx";
import { SettingsHead, useRestPageClass } from "../components/SettingsHead.jsx";
import { Sheet } from "../components/Sheet.jsx";
import { ErrorText, Help, guardComposingEnter, useToast } from "../components/ui.jsx";
import { UNITS_KEY, useUnitList } from "../lib/units.js";
import { useNarrow } from "../lib/useNarrow.js";

const fill = (s: string, v: Record<string, string | number>) => s.replace(/\{(\w+)\}/g, (_, k: string) => String(v[k] ?? ""));
const MOVE_LABELS: Record<MoveKind, string> = { top: "一番上へ", up: UNIT_MSG.moveUp, down: UNIT_MSG.moveDown, bottom: "一番下へ" };

type SheetState = { mode: "add" } | { mode: "rename"; unit: CompanyUnit };
type Ask = { kind: "delete"; unit: CompanyUnit } | { kind: "reset" };

export function SettingsUnitsPage() {
  usePageName(UNIT_MSG.title);
  const back = useBackNav({ to: "/settings", label: "設定" });
  const page = useRestPageClass("unitpage");
  const me = useMe();
  const admin = me.data?.user.role === "admin";
  const narrow = useNarrow();
  const qc = useQueryClient();
  const q = useUnitList();
  const { toast, node: toastNode } = useToast();
  const items = q.data?.items ?? [];
  const full = items.length >= UNIT_LIMIT;

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  const [sheet, setSheet] = useState<SheetState | null>(null);
  const [name, setName] = useState("");
  const [nameErr, setNameErr] = useState<string | undefined>();
  const [ask, setAsk] = useState<Ask | null>(null);
  const [menuId, setMenuId] = useState<string | null>(null);
  /** スマホ：単位名を押して開くシート（名前変更・削除を選ぶ）。acts は閉じる動きのあいだも題に残す */
  const [acts, setActs] = useState<CompanyUnit | null>(null);
  const [actsOpen, setActsOpen] = useState(false);
  const openActs = (u: CompanyUnit) => {
    setActs(u);
    setActsOpen(true);
  };
  /** シートを閉じて、選んだ動き（名前変更のシート・削除の確かめの窓）へ */
  const pickAct = (f: (u: CompanyUnit) => void) => {
    setActsOpen(false);
    if (acts) f(acts);
  };
  const gripEls = useRef(new Map<string, HTMLButtonElement>());

  const set = (list: CompanyUnitList): void => {
    qc.setQueryData(UNITS_KEY, { items: list.items });
  };
  const run = async (f: () => Promise<void>): Promise<boolean> => {
    setBusy(true);
    setErr(undefined);
    try {
      await f();
      return true;
    } catch (e) {
      setErr(messageOf(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  /** 並べ替え：並べたい順の id をまとめて送る。送る前に画面の並びを変える（失敗したら読み直す） */
  const reorder = (ids: string[]) => {
    const byId = new Map(items.map((x) => [x.id, x]));
    qc.setQueryData(UNITS_KEY, { items: ids.flatMap((id, i) => (byId.has(id) ? [{ ...byId.get(id)!, sortNo: (i + 1) * 10 }] : [])) });
    void run(async () => set(await api<CompanyUnitList>("POST", "/units/reorder", { ids }))).then((ok) => {
      if (!ok) void qc.invalidateQueries({ queryKey: UNITS_KEY });
    });
  };
  const moveTo = (id: string, to: number) => {
    const ids = items.map((x) => x.id);
    const from = ids.indexOf(id);
    if (from < 0 || from === to) return;
    snapshot();
    const rest = ids.filter((x) => x !== id);
    rest.splice(Math.max(0, Math.min(to, rest.length)), 0, id);
    reorder(rest);
  };
  const moveBy = (id: string, kind: MoveKind) => {
    const k = items.findIndex((x) => x.id === id);
    const to = kind === "top" ? 0 : kind === "bottom" ? items.length - 1 : kind === "up" ? k - 1 : k + 1;
    if (to < 0 || to >= items.length) return;
    moveTo(id, to);
  };
  const { drag, rows, rowRef, gripProps } = useRowDrag({
    onDrop: (id, to) => moveTo(id, to),
    onTap: (id) => setMenuId((m) => (m === id ? null : id)),
  });
  const snapshot = useFlip(rows);
  const ids = items.map((x) => x.id);

  const openSheet = (s: SheetState) => {
    setSheet(s);
    setName(s.mode === "rename" ? s.unit.name : "");
    setNameErr(undefined);
  };
  const submitName = () => {
    if (!sheet || busy) return;
    const others = items.filter((x) => sheet.mode === "add" || x.id !== sheet.unit.id).map((x) => x.name);
    const e = unitNameError(name, others);
    if (e) {
      setNameErr(e);
      return;
    }
    setBusy(true);
    const req =
      sheet.mode === "add"
        ? api<CompanyUnitList & { message: string }>("POST", "/units", { name })
        : api<CompanyUnitList & { message: string }>("PUT", `/units/${sheet.unit.id}`, { name });
    req
      .then((r) => {
        set(r);
        setSheet(null);
        toast(r.message);
      })
      .catch((x: unknown) => setNameErr(messageOf(x)))
      .finally(() => setBusy(false));
  };
  const confirmAsk = () => {
    if (!ask) return;
    const a = ask;
    void run(async () => {
      const r = a.kind === "delete" ? await api<CompanyUnitList & { message: string }>("DELETE", `/units/${a.unit.id}`) : await api<CompanyUnitList & { message: string }>("POST", "/units/reset");
      set(r);
      toast(r.message);
    }).then(() => setAsk(null));
  };

  return (
    <div className={page} data-testid="units-page">
      <SettingsHead
        back={back}
        title={
          <>
            {UNIT_MSG.title}
            <Help testId="help-units" label="単位の説明" text={UNIT_MSG.help} />
          </>
        }
      />
      {!admin ? <p className="readonly-note">{UNIT_MSG.readonly}</p> : null}
      {admin ? (
        <div className="unittools">
          <button type="button" className="btn primary" id="unitAdd" disabled={full || busy || !q.data} onClick={() => openSheet({ mode: "add" })} data-testid="unit-add">
            {UNIT_MSG.add}
          </button>
          <button type="button" className="btn" id="unitReset" disabled={busy || !q.data} onClick={() => setAsk({ kind: "reset" })} data-testid="unit-reset">
            {UNIT_MSG.reset}
          </button>
        </div>
      ) : null}
      <ErrorText>{err}</ErrorText>
      {q.isError ? <ErrorText>{messageOf(q.error)}</ErrorText> : null}
      {!q.data && !q.isError ? <p className="hint">読み込んでいます…</p> : null}
      {q.data ? (
        <GroupSection
          note={
            <span className={full ? "unitcount is-full" : "unitcount"} data-testid="unit-count">
              {full ? UNIT_MSG.limit : fill(UNIT_MSG.count, { n: items.length, max: UNIT_LIMIT })}
            </span>
          }
          data-testid="units-sec"
        >
          <GroupList className="unitlist" data-testid="unit-list">
            {items.map((u, k) => {
              const target = drag?.target?.id === u.id ? drag.target.where : null;
              const cls = ["grow", "unitrow", drag?.id === u.id && "is-dragging", target === "above" && "is-dropabove", target === "below" && "is-dropbelow", menuId === u.id && "is-open"].filter(Boolean).join(" ");
              const label = fill(UNIT_MSG.grip, { name: u.name });
              return (
                <li key={u.id} ref={rowRef(u.id)} className={cls} style={drag?.id === u.id ? { transform: `translateY(${drag.dy}px)` } : undefined} data-testid="unit-row" data-unit={u.name}>
                  {admin && !narrow ? (
                    <>
                      <GripButton
                        label={label}
                        expanded={menuId === u.id}
                        testId="unit-grip"
                        gripRef={(el: HTMLButtonElement | null) => {
                          if (el) gripEls.current.set(u.id, el);
                          else gripEls.current.delete(u.id);
                        }}
                        {...gripProps(u.id, ids)}
                      />
                      {menuId === u.id ? (
                        <MoveMenu
                          label={label}
                          labels={MOVE_LABELS}
                          canUp={k > 0}
                          canDown={k < items.length - 1}
                          onMove={(kind) => {
                            setMenuId(null);
                            moveBy(u.id, kind);
                            gripEls.current.get(u.id)?.focus();
                          }}
                          onClose={(f) => {
                            setMenuId(null);
                            if (f) gripEls.current.get(u.id)?.focus();
                          }}
                          anchor={() => gripEls.current.get(u.id) ?? null}
                          testId="unit-move-menu"
                        />
                      ) : null}
                    </>
                  ) : null}
                  {admin && narrow ? (
                    <span className="unitmv">
                      <button type="button" className="unitmvbtn" aria-label={`「${u.name}」を${UNIT_MSG.moveUp}`} disabled={k === 0 || busy} onClick={() => moveBy(u.id, "up")} data-testid="unit-up">
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 19V5M6 11l6-6 6 6" /></svg>
                      </button>
                      <button type="button" className="unitmvbtn" aria-label={`「${u.name}」を${UNIT_MSG.moveDown}`} disabled={k === items.length - 1 || busy} onClick={() => moveBy(u.id, "down")} data-testid="unit-down">
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M6 13l6 6 6-6" /></svg>
                      </button>
                    </span>
                  ) : null}
                  {admin && narrow ? (
                    <button type="button" className="unitname unitnamebtn" aria-haspopup="dialog" disabled={busy} onClick={() => openActs(u)} data-testid="unit-open">
                      <span className="unitnametx" data-testid="unit-name">
                        {u.name}
                      </span>
                      <Chevron />
                    </button>
                  ) : (
                    <span className="unitname" data-testid="unit-name">
                      {u.name}
                    </span>
                  )}
                  {admin && !narrow ? (
                    <span className="unitacts">
                      <button type="button" className="btn small text" disabled={busy} onClick={() => openSheet({ mode: "rename", unit: u })} data-testid="unit-rename">
                        {UNIT_MSG.rename}
                      </button>
                      <button type="button" className="btn small text danger" disabled={busy} onClick={() => setAsk({ kind: "delete", unit: u })} data-testid="unit-delete">
                        {UNIT_MSG.delete}
                      </button>
                    </span>
                  ) : null}
                </li>
              );
            })}
          </GroupList>
        </GroupSection>
      ) : null}

      {/* スマホ：単位名を押すと出るシート（題＝単位名・×）。選んだら閉じて、PC と同じ名前変更のシート・削除の確かめの窓へ */}
      <Sheet open={actsOpen} title={acts?.name ?? ""} closeLabel={UNIT_MSG.close} onCancel={() => setActsOpen(false)} testId="unit-acts" closeTestId="unit-acts-close">
        <GroupList>
          <GroupDangerRow tone="ai" title={UNIT_MSG.rename} disabled={busy} onClick={() => pickAct((u) => openSheet({ mode: "rename", unit: u }))} buttonTestId="unit-rename" />
          <GroupDangerRow title={UNIT_MSG.delete} disabled={busy} onClick={() => pickAct((u) => setAsk({ kind: "delete", unit: u }))} buttonTestId="unit-delete" />
        </GroupList>
      </Sheet>

      <Sheet
        open={sheet != null}
        title={sheet?.mode === "rename" ? UNIT_MSG.rename : UNIT_MSG.add}
        cancelLabel={UNIT_MSG.cancel}
        doneLabel={UNIT_MSG.ok}
        doneDisabled={busy}
        onCancel={() => setSheet(null)}
        onDone={submitName}
        testId="unit-sheet"
        doneTestId="unit-sheet-ok"
      >
        <GroupSection>
          <GroupList plain>
            <GroupInputRow id="unitName" label={UNIT_MSG.name} error={nameErr} wide>
              <input
                id="unitName"
                className="giinp"
                maxLength={20}
                autoComplete="off"
                value={name}
                aria-invalid={nameErr ? true : undefined}
                aria-describedby={nameErr ? "unitName-err" : undefined}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameErr(undefined);
                }}
                onKeyDown={guardComposingEnter}
                data-testid="unit-name-input"
              />
            </GroupInputRow>
          </GroupList>
        </GroupSection>
      </Sheet>

      {ask ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && !busy && setAsk(null)}>
          <div className="dlg" role="alertdialog" aria-label={ask.kind === "delete" ? fill(UNIT_MSG.deleteTitle, { name: ask.unit.name }) : UNIT_MSG.resetTitle} aria-describedby="unit-ask-note" onKeyDown={(e) => e.key === "Escape" && !busy && setAsk(null)} data-testid="unit-ask">
            <div className="dh">{ask.kind === "delete" ? fill(UNIT_MSG.deleteTitle, { name: ask.unit.name }) : UNIT_MSG.resetTitle}</div>
            <div className="db">
              <p id="unit-ask-note" style={{ margin: 0 }}>{ask.kind === "delete" ? UNIT_MSG.deleteNote : UNIT_MSG.resetNote}</p>
            </div>
            <div className="df">
              <button type="button" className="btn" autoFocus disabled={busy} onClick={() => setAsk(null)} data-testid="unit-ask-cancel">
                {UNIT_MSG.cancel}
              </button>
              <button type="button" className={ask.kind === "delete" ? "btn is-danger" : "btn primary"} disabled={busy} onClick={confirmAsk} data-testid="unit-ask-ok">
                {UNIT_MSG.ok}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {toastNode}
    </div>
  );
}
